import { useMemo, useState } from "react";
import { Download, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, auDate } from "../../lib/api";
import { useGet } from "../../lib/hooks";
import { useAuth } from "../../lib/auth";
import { Pill } from "../Common";
import { Button } from "../ui/button";
import { RecordDialog } from "../RecordDialog";

const DONE = new Set(["Yes", "Closed", "Complete", "Completed", "Resolved", "Delivered", "Inactive"]);

const show = (f, v) => (v === undefined || v === null || v === "" ? "" : f.type === "date" ? auDate(v) : String(v));

function toCsv(cols, rows) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [cols.map((c) => esc(c.label)).join(","), ...rows.map((r) => cols.map((c) => esc(show(c, r.data?.[c.key]))).join(","))].join("\n");
}

function useFields(schema, people) {
  return useMemo(() => schema.fields.map((f) => {
    const req = f.key === schema.title;
    if (f.type === "auto") return { ...f, type: "readonly" };
    if (f.type === "yesno") return { ...f, type: "select", options: ["Yes", "No"], required: req };
    if (f.type === "person") return { ...f, options: people, required: req };
    if (f.type === "textarea") return { ...f, rows: 3, required: req };
    return { ...f, required: req };
  }), [schema, people]);
}

export function SchemaRegister({ reg }) {
  const s = reg.schema;
  const { user } = useAuth();
  const [rows, reload] = useGet(`/records/${reg.key}`, [reg.key]);
  const [users] = useGet("/users");
  const [edit, setEdit] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("");
  const people = useMemo(() => (users || []).map((u) => u.name || u.email).filter(Boolean), [users]);
  const fields = useFields(s, people);
  const cols = s.fields.filter((f) => f.type !== "heading");
  const fKey = s.group || s.status;
  const fOpts = fKey ? [...new Set((rows || []).map((r) => r.data?.[fKey]).filter(Boolean))] : [];
  const list = (rows || []).filter((r) => (!filter || r.data?.[fKey] === filter) && (!q || Object.values(r.data || {}).join(" ").toLowerCase().includes(q.toLowerCase())));
  const save = async (f) => {
    const { id, ...data } = f;
    try { if (id) await api.put(`/records/${reg.key}/${id}`, { data }); else await api.post(`/records/${reg.key}`, { data }); toast.success("Saved"); setEdit(null); reload(); }
    catch (e) { toast.error(errMsg(e)); throw e; }
  };
  const del = async (r) => { if (!window.confirm("Delete this record?")) return; try { await api.delete(`/records/${reg.key}/${r.id}`); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const exportCsv = () => {
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([toCsv(cols, list)], { type: "text/csv" }));
    a.download = `${reg.label}.csv`; a.click();
  };
  return (
    <div className="space-y-3" data-testid={`register-${reg.key}`}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-[200px] flex-1 sm:max-w-xs"><Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search this register" className="h-9 w-full rounded-md border border-input bg-background pl-9 pr-3 text-sm" data-testid={`register-search-${reg.key}`} /></div>
        {fOpts.length > 0 && <select value={filter} onChange={(e) => setFilter(e.target.value)} className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid={`register-filter-${reg.key}`}>
          <option value="">All {(cols.find((c) => c.key === fKey)?.label || "").toLowerCase()}</option>{fOpts.map((o) => <option key={o}>{o}</option>)}</select>}
        <span className="text-xs text-muted-foreground" data-testid={`register-count-${reg.key}`}>{list.length} record{list.length === 1 ? "" : "s"}</span>
        <div className="ml-auto flex gap-2">
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={!list.length} data-testid={`register-export-${reg.key}`}><Download className="mr-1 h-4 w-4" />Export</Button>
          <Button size="sm" onClick={() => setEdit({})} data-testid={`register-add-${reg.key}`}><Plus className="mr-1 h-4 w-4" />Add record</Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-md border bg-card">
        <table className="w-full text-sm">
          <thead><tr className="border-b">{cols.map((c) => <th key={c.key} className="whitespace-nowrap px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider text-[#EBD6A0]/80">{c.label}</th>)}<th /></tr></thead>
          <tbody>
            {list.map((r) => (
              <tr key={r.id} onClick={() => setEdit({ ...r.data, id: r.id })} className="cursor-pointer border-b border-white/5 transition-colors hover:bg-white/[0.03]" data-testid={`record-${r.id}`}>
                {cols.map((c) => {
                  const v = show(c, r.data?.[c.key]);
                  return <td key={c.key} className="max-w-[240px] truncate px-3 py-2.5" title={v}>{c.key === s.status && v ? <Pill tone={DONE.has(v) ? "green" : "amber"}>{v}</Pill> : v || <span className="text-slate-600">—</span>}</td>;
                })}
                <td className="px-3">{user.role !== "staff" && <button onClick={(e) => { e.stopPropagation(); del(r); }} className="text-slate-500 hover:text-red-400" data-testid={`record-delete-${r.id}`}><Trash2 className="h-4 w-4" /></button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {rows && !list.length && <div className="p-8 text-center text-sm text-muted-foreground">{rows.length ? "No records match." : "No records yet. Click Add record."}</div>}
      </div>
      {edit && <RecordDialog open onOpenChange={(o) => !o && setEdit(null)} title={`${edit.id ? "Edit" : "New"} · ${reg.label}`} fields={fields}
        initial={edit.id ? edit : Object.fromEntries(s.fields.filter((f) => f.type === "auto").map((f) => [f.key, "Assigned when saved"]))} onSave={save} />}
    </div>
  );
}
